import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { memberService, MemberResponse } from '../services/memberService';
import { workoutService } from '../services/workoutService';
import { exerciseService } from '../services/exerciseService';
import { Workout, MemberExerciseRequest } from '../types/Workout';

interface SelectedWorkout {
  workout: Workout;
  member: MemberResponse;
}

const WorkoutCopy: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<MemberResponse[]>([]);
  const [workouts, setWorkouts] = useState<{ [memberId: number]: Workout[] }>({});
  const [selectedWorkouts, setSelectedWorkouts] = useState<SelectedWorkout[]>([]);
  const [targetMembers, setTargetMembers] = useState<number[]>([]);
  const [copying, setCopying] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [targetSearchTerm, setTargetSearchTerm] = useState('');
  const [loadingWorkouts, setLoadingWorkouts] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      setLoading(true);
      const response = await memberService.getMembers({ size: 1000 });
      
      if (response.success && response.data) {
        setMembers(response.data.members);
      } else {
        toast.error('Failed to load members');
      }
    } catch (error) {
      console.error('Error loading members:', error);
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const loadMemberWorkouts = async (memberId: number) => {
    try {
      setLoadingWorkouts(prev => new Set(prev).add(memberId));
      
      const response = await workoutService.getMemberWorkouts(memberId);
      if (response.success && response.data) {
        setWorkouts(prev => ({
          ...prev,
          [memberId]: response.data || []
        }));
      } else {
        setWorkouts(prev => ({
          ...prev,
          [memberId]: []
        }));
        toast.error('Failed to load workouts for this member');
      }
    } catch (error) {
      console.error(`Error loading workouts for member ${memberId}:`, error);
      setWorkouts(prev => ({
        ...prev,
        [memberId]: []
      }));
      toast.error('Failed to load workouts for this member');
    } finally {
      setLoadingWorkouts(prev => {
        const newSet = new Set(prev);
        newSet.delete(memberId);
        return newSet;
      });
    }
  };

  const handleWorkoutSelect = (workout: Workout, member: MemberResponse, isSelected: boolean) => {
    if (isSelected) {
      setSelectedWorkouts(prev => [...prev, { workout, member }]);
    } else {
      setSelectedWorkouts(prev => 
        prev.filter(sw => !(sw.workout.id === workout.id && sw.member.id === member.id))
      );
    }
  };

  const handleTargetMemberSelect = (memberId: number, isSelected: boolean) => {
    if (isSelected) {
      setTargetMembers(prev => [...prev, memberId]);
    } else {
      setTargetMembers(prev => prev.filter(id => id !== memberId));
    }
  };

  const handleCopyWorkouts = async () => {
    if (selectedWorkouts.length === 0) {
      toast.error('Please select at least one workout to copy');
      return;
    }
    
    if (targetMembers.length === 0) {
      toast.error('Please select at least one target member');
      return;
    }

    try {
      setCopying(true);
      let successCount = 0;
      let errorCount = 0;
      const totalOperations = selectedWorkouts.length * targetMembers.length;

      for (const targetMemberId of targetMembers) {
        for (const { workout } of selectedWorkouts) {
          try {
            // Step 1: Create the workout structure
            const workoutRequest = {
              memberId: targetMemberId,
              workoutName: workout.workoutName
            };

            const createResponse = await workoutService.createWorkout(workoutRequest);
            
            if (createResponse.success && createResponse.data) {
              const newWorkoutId = createResponse.data.id;
              
              // Step 2: Get the original workout's exercises
              const exercisesResponse = await exerciseService.getWorkoutExercises(workout.id);
              
              if (exercisesResponse.success && exercisesResponse.data) {
                // Step 3: Copy each exercise to the new workout
                for (const memberExercise of exercisesResponse.data) {
                  try {
                    const exerciseRequest: MemberExerciseRequest = {
                      workoutId: newWorkoutId,
                      exerciseId: memberExercise.exerciseId,
                      sets: memberExercise.sets,
                      orderIndex: memberExercise.orderIndex
                    };

                    await exerciseService.addExerciseToWorkout(exerciseRequest);
                  } catch (exerciseError) {
                    console.error('Error copying exercise:', exerciseError);
                    // Continue copying other exercises even if one fails
                  }
                }
                
                successCount++;
                toast.success(`Copied "${workout.workoutName}" to ${members.find(m => m.id === targetMemberId)?.fullName}`);
              } else {
                // Workout created but exercises copy failed
                successCount++; // Still count as partial success
                console.warn('Workout created but exercises copy failed');
              }
            } else {
              errorCount++;
            }
          } catch (error) {
            console.error('Error copying workout:', error);
            errorCount++;
          }
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully copied ${successCount} of ${totalOperations} workout(s)`);
        // Reset selections
        setSelectedWorkouts([]);
        setTargetMembers([]);
        // Note: Workouts will need to be manually reloaded to see new copies
      }

      if (errorCount > 0) {
        toast.error(`Failed to copy ${errorCount} of ${totalOperations} workout(s)`);
      }

    } catch (error) {
      console.error('Error copying workouts:', error);
      toast.error('Failed to copy workouts');
    } finally {
      setCopying(false);
    }
  };

  const filteredMembers = members.filter(member =>
    member.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.phone.includes(searchTerm)
  );

  const filteredTargetMembers = members.filter(member =>
    member.fullName.toLowerCase().includes(targetSearchTerm.toLowerCase()) ||
    member.phone.includes(targetSearchTerm)
  );

  if (loading) {
    return (
      <div className="u-page workout-copy-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading members and workouts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="u-page workout-copy-container">
      {/* Header */}
      <div className="u-page-header"><div><h2>Copy workouts</h2><p>Reuse an existing training program for one or more members.</p></div></div>

      <div className="copy-workflow">
        {/* Step 1: Select Source Workouts */}
        <div className="step-section">
          <h3>Step 1: Select Workouts to Copy</h3>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
            />
          </div>
          
          <div className="members-workouts-list">
            {filteredMembers.map(member => (
              <div key={member.id} className="member-workouts-card">
                                 <div className="member-header">
                   <div className="member-info">
                     <h4>{member.fullName}</h4>
                     <span className="member-phone">{member.phone}</span>
                   </div>
                  <span className="workout-count">
                    {workouts[member.id] ? `${workouts[member.id].length} workout(s)` : 'Not loaded'}
                  </span>
                </div>
                
                {!workouts[member.id] ? (
                  <div className="load-workouts-section">
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => loadMemberWorkouts(member.id)}
                      disabled={loadingWorkouts.has(member.id)}
                    >
                      {loadingWorkouts.has(member.id) ? '⏳ Loading...' : '📋 Load Workouts'}
                    </button>
                  </div>
                ) : workouts[member.id].length > 0 ? (
                  <div className="workouts-list">
                    {workouts[member.id].map(workout => {
                      const isSelected = selectedWorkouts.some(
                        sw => sw.workout.id === workout.id && sw.member.id === member.id
                      );
                      
                      return (
                        <div key={workout.id} className="workout-item">
                          <label className="workout-checkbox">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => handleWorkoutSelect(workout, member, e.target.checked)}
                            />
                            <div className="workout-details">
                              <span className="workout-name">{workout.workoutName}</span>
                              <span className="workout-meta">
                                {workout.exercises?.length || 0} exercises
                              </span>
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-workouts">
                    <p>No workouts found for this member</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step 2: Select Target Members */}
        <div className="step-section">
          <h3>Step 2: Select Target Members</h3>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search target members..."
              value={targetSearchTerm}
              onChange={(e) => setTargetSearchTerm(e.target.value)}
              className="form-control"
            />
          </div>
          
          <div className="target-members-list">
            {filteredTargetMembers.map(member => {
              const isSelected = targetMembers.includes(member.id);
              
              return (
                <div key={member.id} className="target-member-item">
                  <label className="member-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleTargetMemberSelect(member.id, e.target.checked)}
                    />
                                         <div className="member-details">
                       <span className="member-name">{member.fullName}</span>
                       <span className="member-phone">{member.phone}</span>
                     </div>
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        {/* Summary */}
        {(selectedWorkouts.length > 0 || targetMembers.length > 0) && (
          <div className="copy-summary">
            <h4>Copy Summary</h4>
            <div className="summary-details">
              <div className="summary-item">
                <strong>Workouts to Copy:</strong> {selectedWorkouts.length}
                {selectedWorkouts.length > 0 && (
                  <ul>
                    {selectedWorkouts.map((sw, index) => (
                                             <li key={index}>
                         {sw.workout.workoutName} (from {sw.member.fullName})
                       </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="summary-item">
                <strong>Target Members:</strong> {targetMembers.length}
                {targetMembers.length > 0 && (
                  <ul>
                    {targetMembers.map(memberId => {
                      const member = members.find(m => m.id === memberId);
                      return member ? (
                                                 <li key={memberId}>
                           {member.fullName}
                         </li>
                      ) : null;
                    })}
                  </ul>
                )}
              </div>
              <div className="summary-item">
                <strong>Total Operations:</strong> {selectedWorkouts.length * targetMembers.length}
              </div>
            </div>
          </div>
        )}
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={handleCopyWorkouts}
            disabled={copying || selectedWorkouts.length === 0 || targetMembers.length === 0}
          >
            {copying ? '⏳ Copying...' : `Copy ${selectedWorkouts.length} Workout(s)`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkoutCopy; 
